/**
 * NavService — the single path request queue (Movement spec §2.2). Agents NEVER call
 * A* directly; they queue through here so the cost is budgeted and shared.
 *
 * - Priority: combat-visible (0) > visible idle (1) > off-screen (2).
 * - Dedup: a new request from an agent replaces its pending one — never two paths
 *   queued for one agent.
 * - Per-frame budget: tick(budgetMs) drains the queue in priority order until the
 *   budget is spent; leftovers roll to the next frame. A stall is never worth it.
 *
 * Off-threading: the spec wants a Web Worker. The pathfinding here is pure and
 * self-contained, so moving it to a worker is a drop-in later (post a request, get a
 * PathResult back). For now it runs time-sliced on the main thread via tick(); the
 * budget cap keeps a frame from blowing out. Flagged, not hidden.
 */

import { NavGrid } from './grid';
import { findPath, type PathResult, type PathOptions } from './pathfind';
import { smoothPath, type Pt } from './postprocess';

export type Priority = 0 | 1 | 2;

export interface PathRequestOptions extends PathOptions {
  priority?: Priority;
  /** Post-process imperfection (per tier); needs an rng to fire. */
  sigma?: number;
  rng?: () => number;
}

export interface QueuedResult extends PathResult {
  /** True if a newer request from the same agent replaced this one. */
  superseded?: boolean;
}

interface Req {
  agentId: string;
  sx: number; sy: number; gx: number; gy: number;
  opts: PathRequestOptions;
  seq: number;
  resolve: (r: QueuedResult) => void;
}

const superseded: QueuedResult = { path: [], found: false, partial: false, unreachable: false, expanded: 0, superseded: true };

export class NavService {
  private queue = new Map<string, Req>();   // one pending request per agent (dedup)
  private seq = 0;
  private processedLastTick = 0;

  constructor(private grid: NavGrid, private now: () => number = () => performance.now()) {}

  /** Queue a path request; resolves when the queue processes it (or is superseded). */
  requestPath(agentId: string, sx: number, sy: number, gx: number, gy: number, opts: PathRequestOptions = {}): Promise<QueuedResult> {
    const prev = this.queue.get(agentId);
    if (prev) prev.resolve(superseded);   // replace the pending one
    return new Promise<QueuedResult>((resolve) => {
      this.queue.set(agentId, { agentId, sx, sy, gx, gy, opts, seq: this.seq++, resolve });
    });
  }

  /** Drop an agent's pending request (e.g. it died). */
  cancel(agentId: string) {
    const r = this.queue.get(agentId);
    if (r) { r.resolve(superseded); this.queue.delete(agentId); }
  }

  pending() { return this.queue.size; }
  lastProcessed() { return this.processedLastTick; }

  /** Process queued requests in priority order until `budgetMs` is spent. */
  tick(budgetMs = 2): number {
    if (!this.queue.size) { this.processedLastTick = 0; return 0; }
    const reqs = [...this.queue.values()].sort((a, b) =>
      (a.opts.priority ?? 1) - (b.opts.priority ?? 1) || a.seq - b.seq);
    const start = this.now();
    let done = 0;
    for (const req of reqs) {
      // Always process at least one so a tiny budget still makes progress.
      if (done > 0 && this.now() - start >= budgetMs) break;
      const raw = findPath(this.grid, req.sx, req.sy, req.gx, req.gy, req.opts);
      let path: Pt[] = raw.path;
      if (raw.path.length > 2) path = smoothPath(this.grid, raw.path, { sigma: req.opts.sigma, rng: req.opts.rng });
      req.resolve({ ...raw, path });
      this.queue.delete(req.agentId);
      done++;
    }
    this.processedLastTick = done;
    return done;
  }
}
